import React, { useCallback, useEffect, useMemo, useState } from 'react';
import portalpaymentlink from '../../services/portalpaymentlink';
import { Box, Container, Tab, Tabs, Stack, Paper, Typography, Button, FormControlLabel, Checkbox, TextField, Divider } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { getProfileColumns } from '../../utils/profileColumns';
import Swal from 'sweetalert2';
import GetCookieByName from '../../hooks/GetCookie';
import jwtDecode from 'jwt-decode';
import LinkStatusCard from './common/LinkStatusCard';
import helpers from '../../hooks/HelperFunctions';
import { getClubLinksCampaignColumns } from '../../utils/clubLinksCampaignsColumns';
import { toast } from 'react-toastify';
import paymentportalservice from '../../services/paymentportalservice';

const STATUS_MAP = {
    pending: "P",
    applied: "A",
    waiting: "W",
    success: "S",
    failed_or_rejected: ["F", "R"],
};

const CHILD_TABLE_COLUMNS = new Set([
    "Date",
    "Campaign",
    "Platform",
    "Influencer",
    "POC",
    "Total Amount",
    "GST Amount",
    "%",
    "Payable Amount"
]);

function CustomToolbar() {
    return (
        <GridToolbarContainer
            sx={{
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
            }}
        >
            <GridToolbarExport />
        </GridToolbarContainer>
    );
}

const OverviewTab = React.memo(function OverviewTab({
    overview,
    handleClick
}) {
    const columns = useMemo(() => {
        return getClubLinksCampaignColumns();
    }, []);
    console.log(overview);
    return (
        <Stack
            rowGap={4}
        >
            {/* Status Cards */}
            <Stack
                direction={"row"}
                columnGap={2}
                rowGap={2}
                flexWrap={'wrap'}
            >
                {/* cards */}
                <LinkStatusCard
                    label={"Pending"}
                    value={overview?.stats?.totalPending}
                    action={overview?.stats?.totalPending > 0 ? () => { handleClick("pending", "SWITCH_TAB") } : null}
                />

                <LinkStatusCard
                    label={"Applied"}
                    value={overview?.stats?.totalApplied}
                    action={overview?.stats?.totalApplied > 0 ? () => { handleClick("applied", "SWITCH_TAB") } : null}
                />

                <LinkStatusCard
                    label={"Success"}
                    value={overview?.stats?.totalSuccess}
                    action={overview?.stats?.totalSuccess > 0 ? () => { handleClick("success", "SWITCH_TAB") } : null}
                />

                <LinkStatusCard
                    label={"Failed"}
                    value={overview?.stats?.totalFailed}
                    action={overview?.stats?.totalFailed > 0 ? () => { handleClick("failed_or_rejected", "SWITCH_TAB") } : null}
                />

                <LinkStatusCard
                    label={"Rejected"}
                    value={overview?.stats?.totalRejected}
                    action={overview?.stats?.totalRejected > 0 ? () => { handleClick("failed_or_rejected", "SWITCH_TAB") } : null}
                />

                <LinkStatusCard
                    label={"Total Campaigns"}
                    value={overview?.totalCampaignCount}
                />

                <LinkStatusCard
                    label={"Total Paid"}
                    value={helpers.formatNumber(overview?.totalSuccessAmount, true)}
                />
            </Stack>

            {/* Campaign Table */}
            <Stack>
                <Typography
                    sx={{
                        fontWeight: 600
                    }}
                >
                    Campaigns List
                </Typography>

                <Divider sx={{ my: 2 }} />

                {
                    overview?.totalCampaigns?.length > 0 &&
                    <DataGrid
                        columns={columns}
                        rows={overview?.totalCampaigns}
                        getRowId={(row) => row._id}

                        showCellVerticalBorder
                        showColumnVerticalBorder

                        density='compact'

                        initialState={{
                            pagination: {
                                paginationModel: {
                                    page: 0,
                                    pageSize: 10
                                }
                            }
                        }}
                        pageSizeOptions={[10]}
                    />
                }
            </Stack>
        </Stack>
    );
});

const ProfileDetails = React.memo(function ProfileDetails({ displayDetails }) {
    if (!displayDetails) {
        return <></>
    }

    const isVen = displayDetails.isVendor;

    return (
        <Stack
            p={1.5}
        >
            <Typography
                fontSize={15}
                fontWeight={700}
            >
                {isVen ? displayDetails?.name : displayDetails?.username}
            </Typography>

            <Divider sx={{ my: 1 }} />
        </Stack>
    )
});

function ClubLinksProfile({ id, isVen }) {
    const [loading, setLoading] = useState(true);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [search, setSearch] = useState("");

    const [profile, setProfile] = useState({
        display: null,
        overview: null,
        details: []
    });

    const [activeTab, setActiveTab] = useState("overview");
    const [childLinks, setChildLinks] = useState([]);

    const token = JSON.parse(GetCookieByName("auth"))["token"];
    const decoded = jwtDecode(token);
    const { isAdmin, isSuperUser } = decoded;

    const Init = useCallback(async () => {
        try {
            setLoading(true);

            const postdata = {
                id,
                isCompany: isVen
            };

            const displayPromise = isVen
                ? portalpaymentlink.getVendorDetails({ id })
                : portalpaymentlink.getInfluencerDetails(id);

            const [
                display,
                overview,
                details
            ] = await Promise.all([
                displayPromise,
                portalpaymentlink.getProfileOverview(postdata),
                portalpaymentlink.getProfileDetails(postdata)
            ]);

            let displayDetails = { isVendor: isVen };

            if (typeof display === "string") {
                displayDetails["name"] = display;
            }

            if (typeof display === 'object') {
                displayDetails = { ...displayDetails, ...display };
            }

            setProfile({
                display: displayDetails,
                overview,
                details
            });
        }
        catch (error) {
            console.error(error);
        }
        finally {
            setLoading(false);
        }
    }, [id, isVen]);

    const handleCopy = useCallback((row) => {
        if (!row) {
            return;
        }

        let targeturl = process.env.REACT_APP_FORM_BASE_URL;

        if (row.isCompany) {
            targeturl += "/company";
        }
        else {
            targeturl += "/influencer"
        }

        targeturl += `/payment/${row.pathName}`;
        navigator.clipboard.writeText(targeturl);

        toast.success("Link Copied!");
    }, []);

    const handleDelete = useCallback(async (row) => {
        if (!row) {
            return;
        }

        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });

        if (!result.isConfirmed) {
            return;
        }

        await paymentportalservice.DeletePaymentLink(row?._id, row?.payableAmount);

        await Swal.fire({
            title: "Deleted!",
            text: "Your payment link has been deleted.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
        });
    }, []);

    const handleViewChildLinks = useCallback(async (row) => {
        setChildLinks([]);
        if (!row) {
            return;
        }

        const response = await paymentportalservice.ViewChildPaymentLinks({ linkChild: row?.linkChild });

        if (response?.length) {
            setChildLinks(response);
        }
    }, []);

    useEffect(() => {
        Init();
    }, [Init]);

    const rowsByStatus = useMemo(() => {
        const map = {};

        profile.details?.forEach(group => {
            map[group._id] = group.details || [];
        });

        return map;
    }, [profile.details]);

    const currentRows = useMemo(() => {
        if (activeTab === "failed_or_rejected") {
            return [
                ...(rowsByStatus.F || []),
                ...(rowsByStatus.R || [])
            ];
        }

        const code = STATUS_MAP[activeTab];
        return rowsByStatus[code] || [];
    }, [activeTab, rowsByStatus]);

    const filteredRows = useMemo(() => {
        const keyword = search.trim().toLowerCase();

        if (!keyword) return currentRows;

        return currentRows.filter(row => {
            return (
                row.campaign?.name?.toLowerCase().includes(keyword) ||
                row.influencer?.username?.toLowerCase().includes(keyword) ||
                row.platform?.some(p =>
                    p.name.toLowerCase().includes(keyword)
                ) ||
                String(row.cost).includes(keyword) ||
                String(row.payableAmount).includes(keyword)
            );
        });
    }, [currentRows, search]);

    const tabs = useMemo(() => {
        const result = [
            {
                value: "overview",
                label: "Overview"
            }
        ];

        if (rowsByStatus.P?.length)
            result.push({
                value: "pending",
                label: `Pending (${rowsByStatus.P.length})`
            });

        if (rowsByStatus.A?.length)
            result.push({
                value: "applied",
                label: `Applied (${rowsByStatus.A.length})`
            });

        if (rowsByStatus.W?.length)
            result.push({
                value: "waiting",
                label: `Waiting (${rowsByStatus.W.length})`
            });

        if (rowsByStatus.S?.length)
            result.push({
                value: "success",
                label: `Success (${rowsByStatus.S.length})`
            });

        const failedCount =
            (rowsByStatus.F?.length || 0) +
            (rowsByStatus.R?.length || 0);

        if (failedCount)
            result.push({
                value: "failed_or_rejected",
                label: `Failed / Rejected (${failedCount})`
            });

        return result;
    }, [rowsByStatus]);

    const columns = useMemo(() => {
        return getProfileColumns({
            isVendor: isVen,
            onCopy: handleCopy,
            onDelete: handleDelete,
            onViewChildLinks: handleViewChildLinks,
            isAdmin: isAdmin,
            isSuperUser: isSuperUser
        });
    }, [
        isVen,
        handleCopy,
        handleDelete,
        handleViewChildLinks,
        isAdmin,
        isSuperUser
    ]);

    const childColumns = useMemo(() => {
        return columns.filter(c => CHILD_TABLE_COLUMNS.has(c.headerName));
    }, [columns]);

    const selectionSummary = useMemo(() => {
        const ids = new Set(rowSelectionModel);

        let payable = 0;
        let cost = 0;
        let gst = 0;

        const rows = [];

        filteredRows.forEach(row => {
            if (!ids.has(row._id)) return;

            rows.push(row);

            payable += Number(row.payableAmount || 0);
            cost += Number(row.cost || 0);
            gst += Number(row.gstAmount || 0);
        });

        return {
            rows,
            count: rows.length,
            payable,
            cost,
            gst
        };
    }, [filteredRows, rowSelectionModel]);

    const handleSelectAll = useCallback((checked) => {
        if (checked) {
            setRowSelectionModel(filteredRows.map(row => row._id));
        } else {
            setRowSelectionModel([]);
        }
    }, [filteredRows]);

    const isAllSelected =
        filteredRows.length > 0 &&
        rowSelectionModel.length === filteredRows.length;

    const isIndeterminate =
        rowSelectionModel.length > 0 &&
        rowSelectionModel.length < filteredRows.length;

    useEffect(() => {
        setRowSelectionModel([]);
    }, [search, activeTab]);

    const handleClubLinks = useCallback(async () => {
        const userSelection = await Swal.fire({
            title: "Combine payment links",
            html: `
                <div style="font-size: 14px; font-family: 'Quicksand'">
                    You are combining <b>${selectionSummary.count}</b> payment links into a single payment link.
                    <br/>
                    The total payable amount will be <b>${selectionSummary.payable}</b>
                    <br/><br/>
                    Would you like to proceed?
                </div>
            `,
            icon: "question",

            showCancelButton: true,
            showDenyButton: true,

            cancelButtonText: "Cancel",
            denyButtonText: "Confirm without GST",
            confirmButtonText: "Proceed with GST",

            confirmButtonColor: "#2e7d32",
            denyButtonColor: "#1565c0",
            cancelButtonColor: "#6b7280",

            reverseButtons: true
        });

        if (userSelection.isDismissed) {
            return;
        }

        let postdata = {
            ids: selectionSummary.rows.map(row => row._id),
            createdby: decoded._id
        }

        if (userSelection.isDenied) postdata['isGst'] = false;

        if (userSelection.isConfirmed) postdata['isGst'] = true;

        console.log(`postdata: `, postdata);

        const response = await portalpaymentlink.createClubLink(postdata);

        if (response?.status === "ok") {
            window.location.href = "/payments/status";
        }
    }, [selectionSummary, decoded._id]);

    const handleOverviewCardsClick = useCallback((cardName, action) => {
        if (action === "SWITCH_TAB") {
            setActiveTab(cardName);
            return;
        }
    }, []);

    return (
        <Container
            maxWidth={false}
            disableGutters
            sx={{
                px: 2
            }}
        >
            {/* Show Profile details before tab */}
            <ProfileDetails
                displayDetails={profile.display}
            />

            {/* Show tabs */}
            <Tabs
                value={activeTab}
                onChange={(e, value) => { setActiveTab(value); setChildLinks([]); }}
            >
                {tabs.map(tab => (
                    <Tab
                        key={tab.value}
                        value={tab.value}
                        label={tab.label}
                    />
                ))}
            </Tabs>

            {/* Show grid or cards based on status */}
            <Box
                mt={2}
            >
                {activeTab === "overview" ? (
                    <OverviewTab
                        overview={profile.overview}
                        handleClick={handleOverviewCardsClick}
                    />
                ) : (
                    <Stack
                        rowGap={1}
                    >
                        <Paper sx={{ p: 1.5, mb: 2 }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Stack
                                    direction="row"
                                    spacing={3}
                                    alignItems="center"
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isAllSelected}
                                                indeterminate={isIndeterminate}
                                                onChange={(e) =>
                                                    handleSelectAll(e.target.checked)
                                                }
                                            />
                                        }
                                        label={`Select All (${filteredRows.length})`}
                                    />

                                    <Typography>
                                        {selectionSummary.count} Selected
                                    </Typography>

                                    <Typography>
                                        ₹{selectionSummary.payable}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant='outlined'
                                        disabled={selectionSummary.count <= 0 || activeTab !== "pending"}
                                        onClick={handleClubLinks}
                                    >
                                        Club Links
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        <TextField
                            size="small"
                            placeholder="Search Campaign || POC || Amount || Influencer"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{ width: "35%" }}
                        />

                        <Box
                            sx={{
                                height: 600
                            }}
                        >
                            <DataGrid
                                rows={filteredRows}
                                columns={columns}
                                getRowId={(row) => row._id}

                                checkboxSelection
                                disableRowSelectionOnClick

                                rowSelectionModel={rowSelectionModel}
                                onRowSelectionModelChange={setRowSelectionModel}

                                hideFooter
                                density="standard"

                                disableColumnMenu
                                disableColumnSelector
                                disableDensitySelector

                                showCellVerticalBorder
                                showColumnVerticalBorder

                                slots={{
                                    toolbar: CustomToolbar
                                }}
                            />
                        </Box>
                    </Stack>
                )}
            </Box>

            {/* Section for showing child links */}
            {
                childLinks.length > 0 &&
                <Box
                    sx={{
                        mt: 2,
                        py: 2
                    }}
                >
                    <DataGrid
                        columns={childColumns}
                        rows={childLinks}
                        getRowId={(row) => row._id}

                        disableColumnMenu
                        disableColumnSelector
                        disableDensitySelector

                        showCellVerticalBorder
                        showColumnVerticalBorder

                        density="compact"

                        slots={{
                            toolbar: CustomToolbar
                        }}
                    />
                </Box>
            }
        </Container>
    )
}

export default ClubLinksProfile;